import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { UMLoader } from '@urbanmetrix/um-kit';
import { getApiErrorMessage } from 'api/apiClient';
import { ViewContext } from 'components/lib';
import { ExpandIcon, ReloadIcon } from 'components/icons';
import { VISUALIZER_PREVIEW_RELOAD_EVENT } from 'constants/grid';

type MapPreviewMode = '2d' | '3d';

interface MapPreviewModeFrameProps {
  children: ReactNode;
  showSwitch?: boolean;
  gridId?: string | number | null;
  isPreviewLoading?: boolean;
  onUpdatePreview?: (() => void | Promise<void>) | null;
  hasUnsavedChanges?: boolean;
  hasValidationErrors?: boolean;
}

const shellStyle = {
  position: 'relative',
  height: '100%',
  minHeight: 0,
} as const;

const switcherStyle = {
  position: 'absolute',
  top: '12px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1000,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0',
  padding: '4px',
  borderRadius: '999px',
  background: '#ffffff',
  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.14)',
} as const;

const contentStyle = {
  position: 'relative',
  height: '100%',
  minHeight: 0,
} as const;

const panelStyle = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
} as const;

const buttonStyle = {
  minWidth: '56px',
  padding: '6px 14px',
  borderRadius: '999px',
  color: '#64748b',
  fontSize: '0.875rem',
  fontWeight: 700,
  lineHeight: 1,
  textTransform: 'none',
} as const;

const activeButtonStyle = {
  background: '#2f80ed',
  color: '#ffffff',
} as const;

const iframeStyle = {
  width: '100%',
  height: '100%',
  minHeight: 0,
  border: 0,
  borderRadius: '18px',
  background: '#f8fafc',
} as const;

const previewButtonStyle = {
  position: 'absolute',
  top: '12px',
  right: '12px',
  zIndex: 1000,
  minWidth: '154px',
  padding: '10px 16px',
  borderRadius: '999px',
  background: '#2f80ed',
  color: '#ffffff',
  fontSize: '0.875rem',
  fontWeight: 700,
  fontFamily: 'inherit',
  lineHeight: 1,
  textTransform: 'none',
  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.14)',
  cursor: 'pointer',
} as const;

const expandButtonStyle = {
  position: 'absolute',
  top: '12px',
  left: '12px',
  zIndex: 1000,
  minWidth: '40px',
  width: '40px',
  height: '40px',
  padding: '0',
  borderRadius: '999px',
  background: 'rgba(255, 255, 255, 0.82)',
  color: '#64748b',
  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.14)',
  backdropFilter: 'blur(6px)',
  cursor: 'pointer',
} as const;

const previewButtonContentStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
} as const;

const loaderShellStyle = {
  width: '100%',
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  borderRadius: '18px',
  background: '#f8fafc',
} as const;

const loaderTextStyle = {
  color: '#475569',
  fontSize: '0.875rem',
  fontWeight: 600,
  lineHeight: 1.2,
} as const;

const MapPreviewModeFrame = ({
  children,
  showSwitch = true,
  gridId = null,
  isPreviewLoading = false,
  onUpdatePreview = null,
  hasUnsavedChanges = false,
  hasValidationErrors = false,
}: MapPreviewModeFrameProps) => {
  const viewContext = useContext(ViewContext);
  const [mode, setMode] = useState<MapPreviewMode>('3d');
  const [isUpdatePreviewPending, setIsUpdatePreviewPending] = useState(false);
  const [isIframeReady, setIsIframeReady] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const attachIframe = useCallback((iframe: HTMLIFrameElement | null) => {
    iframeRef.current = iframe;
    setIsIframeReady(false);
  }, []);
  const visualizerUrl = useMemo(() => {
    if (!gridId) {
      return 'about:blank';
    }

    const encodedGridId = encodeURIComponent(String(gridId));
    const baseUrl = new URL('/visualizer/', window.location.origin);

    /*if (window.location.hostname === 'localhost') {
      baseUrl.port = '5500';
    }*/

    baseUrl.searchParams.set('grid_id', encodedGridId);
    baseUrl.searchParams.set('preview', 'true');

    return baseUrl.toString();
  }, [gridId]);
  const visualizerOrigin = useMemo(
    () => (visualizerUrl === 'about:blank' ? null : new URL(visualizerUrl).origin),
    [visualizerUrl]
  );
  const visualizerExpandedUrl = useMemo(() => {
    if (visualizerUrl === 'about:blank') {
      return null;
    }

    const expandedUrl = new URL(visualizerUrl);
    expandedUrl.searchParams.delete('preview');
    return expandedUrl.toString();
  }, [visualizerUrl]);

  const reloadPreview = useCallback(() => {
    if (!iframeRef.current?.contentWindow || !visualizerOrigin) {
      return;
    }

    iframeRef.current.contentWindow.postMessage({ type: 'RELOAD' }, visualizerOrigin);
    setIsIframeReady(false);
  }, [visualizerOrigin]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== visualizerOrigin || event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type === 'VISUALIZER_PREVIEW_LOADING') setIsIframeReady(false);
      if (
        event.data?.type === 'VISUALIZER_PREVIEW_READY' ||
        event.data?.type === 'VISUALIZER_PREVIEW_ERROR' ||
        event.data?.type === 'VISUALIZER_PREVIEW_BLOCKED'
      ) {
        setIsIframeReady(true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [visualizerOrigin]);

  useEffect(() => {
    const handleReload = () => {
      reloadPreview();
    };

    window.addEventListener(VISUALIZER_PREVIEW_RELOAD_EVENT, handleReload);

    return () => {
      window.removeEventListener(VISUALIZER_PREVIEW_RELOAD_EVENT, handleReload);
    };
  }, [reloadPreview]);

  const isPreviewActionLoading = isUpdatePreviewPending || isPreviewLoading;
  const shouldRenderIframe = gridId != null && !isPreviewActionLoading;
  const isPreviewActionDisabled =
    isPreviewActionLoading || hasValidationErrors || !hasUnsavedChanges || !onUpdatePreview;
  const resolvedPreviewButtonStyle = useMemo(
    () =>
      hasUnsavedChanges && !hasValidationErrors
        ? previewButtonStyle
        : {
            ...previewButtonStyle,
            background: '#ffffff',
            color: '#94a3b8',
            cursor: 'not-allowed',
            opacity: 0.6,
          },
    [hasUnsavedChanges, hasValidationErrors]
  );

  const handleUpdatePreviewClick = useCallback(async () => {
    if (!onUpdatePreview || isPreviewActionDisabled) {
      return;
    }

    try {
      setIsUpdatePreviewPending(true);
      await onUpdatePreview();
    } catch (error) {
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to update preview. Check the data and try again.'),
        'error',
        true,
        'toast'
      );
    } finally {
      setIsUpdatePreviewPending(false);
    }
  }, [isPreviewActionDisabled, onUpdatePreview, viewContext.notification]);

  const handleOpenExpandedPreview = useCallback(() => {
    if (!visualizerExpandedUrl) {
      return;
    }

    window.open(visualizerExpandedUrl, '_blank', 'noopener,noreferrer');
  }, [visualizerExpandedUrl]);

  return (
    <div className="grid-map-preview-frame" style={shellStyle}>
      {showSwitch ? (
        <div style={switcherStyle}>
          <Button
            type="button"
            onClick={() => setMode('2d')}
            style={mode === '2d' ? { ...buttonStyle, ...activeButtonStyle } : buttonStyle}
          >
            2D
          </Button>
          <Button
            type="button"
            onClick={() => setMode('3d')}
            style={mode === '3d' ? { ...buttonStyle, ...activeButtonStyle } : buttonStyle}
          >
            3D
          </Button>
        </div>
      ) : null}

      <div style={contentStyle}>
        <div
          style={{
            ...panelStyle,
            visibility: mode === '2d' ? 'visible' : 'hidden',
            pointerEvents: mode === '2d' ? 'auto' : 'none',
            zIndex: mode === '2d' ? 1 : 0,
          }}
        >
          {children}
        </div>
        {mode === '3d' && !isPreviewActionLoading && visualizerExpandedUrl ? (
          <Button
            type="button"
            onClick={handleOpenExpandedPreview}
            style={expandButtonStyle}
            className="grid-map-expand-button"
          >
            <ExpandIcon style={{ cursor: 'pointer' }} />
          </Button>
        ) : null}
        {mode === '3d' && !isPreviewLoading ? (
          <Button
            type="button"
            onClick={() => void handleUpdatePreviewClick()}
            style={resolvedPreviewButtonStyle}
            className="grid-map-preview-button"
            disabled={isPreviewActionDisabled}
          >
            <span style={previewButtonContentStyle}>
              {isPreviewActionLoading ? (
                <CircularProgress size={16} thickness={6} sx={{ color: '#ffffff' }} />
              ) : (
                <ReloadIcon
                  className="untitled-icons"
                  style={{
                    color: hasUnsavedChanges && !hasValidationErrors ? '#ffffff' : '#94a3b8',
                    width: 16,
                    height: 16,
                  }}
                />
              )}
              <span>Update Preview</span>
            </span>
          </Button>
        ) : null}
        {mode === '3d' && (isPreviewActionLoading || !isIframeReady) ? (
          <div style={{ ...loaderShellStyle, ...panelStyle, zIndex: 1 }}>
            <UMLoader />
            <div style={loaderTextStyle}>
              {isPreviewActionLoading ? 'Preparing geodata...' : 'Loading 3D preview...'}
            </div>
          </div>
        ) : null}
        {shouldRenderIframe ? (
          <iframe
            key={visualizerUrl}
            ref={attachIframe}
            title="3D Preview"
            src={visualizerUrl}
            style={{
              ...iframeStyle,
              ...panelStyle,
              visibility: mode === '3d' && isIframeReady ? 'visible' : 'hidden',
              pointerEvents: mode === '3d' && isIframeReady ? 'auto' : 'none',
              zIndex: mode === '3d' && !isPreviewLoading ? 1 : 0,
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

export default MapPreviewModeFrame;
export { VISUALIZER_PREVIEW_RELOAD_EVENT } from 'constants/grid';
